import React from 'react'
import { Text } from '@chakra-ui/react'
import { Icon } from '@chakra-ui/react'
import { Button } from '@chakra-ui/react'
import { ChevronDownIcon } from '@chakra-ui/icons'
import { Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react'

import { getCleanEnum } from '~utils/helpers'
import { ChatStatus } from '~graphql/generated/graphql'
import { CHAT_STATUS_COLORS, CHAT_STATUS_ICONS } from '../../constants'
import IconButtonWithTooltip from '~components/shared/icon-button-with-tooltip'

interface Props {
    chatStatus: ChatStatus
    onSelectChatStatus: (chatStatus: ChatStatus) => void
    variant?: 'icon' | 'full'
}

export default function ChatStatusSelect({
    chatStatus,
    onSelectChatStatus,
    variant = 'full',
}: Props) {
    const options = [
        ChatStatus.Open,
        ChatStatus.Closed,
        ChatStatus.HighPriority,
        ChatStatus.NormalPriority,
        ChatStatus.LowPriority,
    ]

    function modifyText(value: string) {
        if (value === ChatStatus.NormalPriority) return 'Medium Priority'
        return getCleanEnum(value)
    }

    return (
        <Menu>
            <TriggerComponent chatStatus={chatStatus} variant={variant} />
            <MenuList>
                {options.map(option => (
                    <MenuItem
                        key={option}
                        icon={<ChatStatusIcon chatStatus={option} />}
                        color={`${CHAT_STATUS_COLORS[option]}.500`}
                        onClick={() => onSelectChatStatus(option)}
                    >
                        <Text fontSize="sm" fontWeight="medium">
                            {getCleanEnum(option)}
                        </Text>
                    </MenuItem>
                ))}
            </MenuList>
        </Menu>
    )
}

function ChatStatusIcon({ chatStatus }: { chatStatus: ChatStatus }) {
    return <Icon as={CHAT_STATUS_ICONS[chatStatus]} fontSize="14px" />
}

function TriggerComponent({
    chatStatus,
    variant,
}: Omit<Props, 'onSelectChatStatus'>) {
    if (variant === 'icon') {
        return (
            <MenuButton
                as={IconButtonWithTooltip}
                variant="ghost"
                size="xs"
                color={`${CHAT_STATUS_COLORS[chatStatus]}.500`}
                _hover={{
                    bg: 'transparent',
                    color: `${CHAT_STATUS_COLORS[chatStatus]}.600`,
                }}
                _active={{
                    bg: 'transparent',
                    color: `${CHAT_STATUS_COLORS[chatStatus]}.600`,
                }}
                icon={<ChatStatusIcon chatStatus={chatStatus} />}
                label={`Status: ${getCleanEnum(chatStatus)}`}
            />
        )
    }
    return (
        <MenuButton
            as={Button}
            variant="solid"
            size="xs"
            h="28px"
            rounded="full"
            colorScheme={CHAT_STATUS_COLORS[chatStatus]}
            leftIcon={<ChatStatusIcon chatStatus={chatStatus} />}
            rightIcon={<ChevronDownIcon />}
        >
            {getCleanEnum(chatStatus)}
        </MenuButton>
    )
}
